import { useQuery } from '@tanstack/react-query';
import { AI_CAPABILITIES, AI_CAPABILITY_LABELS, type AiCapability } from '@lixionary-jira/domain';
import { Copy } from 'lucide-react';
import { useState } from 'react';
import { useParams, useSearchParams } from 'react-router';
import { ApiError } from '../../api/client';
import { api, useAction } from '../../api/hooks';
import { useIsAdmin } from '../../api/session';
import type { AiServiceDetail } from '../../api/types';
import { useCrumbs } from '../../layout/crumbs';
import { Badge, StateBadge } from '../../ui/Badge';
import { Button } from '../../ui/Button';
import { Alert, ErrorAlert, PageUnavailable, QueryView } from '../../ui/feedback';
import { Field, Input, Textarea } from '../../ui/forms';
import { ConfirmDialog, Modal } from '../../ui/Modal';
import { ActorName, Card, KeyValue, PageHeader, Tabs, usePageTitle } from '../../ui/misc';
import { Timestamp } from '../../ui/Timestamp';
import { useToast } from '../../ui/toast';
import { AgentSetupGuide } from '../shared/AgentSetupGuide';

/**
 * IDN-06, MCP-02: one AI agent in two tabs. Project access: every project with a checkbox per capability
 * (read-only for personal agents, which mirror their owner). Credentials & setup: credentials (secrets
 * shown once) or sign-in connections, plus the Claude Code plugin setup guide.
 */
type AgentTab = 'access' | 'credentials';

export function AiServiceDetailPage() {
  const { serviceId } = useParams();
  const admin = useIsAdmin();
  const q = useQuery({
    queryKey: ['ai-service', serviceId],
    queryFn: () => api.get<AiServiceDetail>(`/admin/ai-services/${serviceId}`),
    enabled: admin,
  });
  usePageTitle(q.data ? `${q.data.name} · AI agent` : null);
  useCrumbs([{ label: 'AI agents', to: '/admin/ai-agents' }, { label: q.data?.name ?? '' }]);
  const [issuing, setIssuing] = useState(false);
  const [revoking, setRevoking] = useState<string | null>(null);
  const [toggle, setToggle] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [params, setParams] = useSearchParams();
  const tab: AgentTab = params.get('tab') === 'credentials' ? 'credentials' : 'access';
  const setTab = (t: AgentTab) => setParams(t === 'access' ? {} : { tab: t }, { replace: true });
  const inv = [['ai-service', serviceId], ['ai-services']];
  const revoke = useAction(
    (cid: string) => api.post(`/admin/ai-services/${serviceId}/credentials/${cid}/revoke`),
    {
      success: q.data?.owner ? 'Connection revoked' : 'Credential revoked',
      invalidate: inv,
      onSuccess: () => setRevoking(null),
    },
  );
  const setActive = useAction(
    (active: boolean) =>
      api.patch(`/admin/ai-services/${serviceId}`, { active, expectedVersion: q.data!.version }),
    { success: 'Agent updated', invalidate: inv, onSuccess: () => setToggle(false) },
  );
  if (!admin) return <PageUnavailable />;
  return (
    <div className="page">
      <QueryView query={q}>
        {(s) => (
          <>
            <PageHeader
              title={s.name}
              subtitle={
                s.owner ? (
                  <>
                    Personal agent of <ActorName actor={s.owner} />. It reads and writes exactly their
                    projects and connects by browser sign-in.
                  </>
                ) : (
                  s.description || 'Headless agent'
                )
              }
              actions={
                <>
                  {s.active ? <Badge tone="success">Active</Badge> : <Badge>Inactive</Badge>}
                  {!s.owner && (
                    <Button variant="secondary" size="sm" onClick={() => setRenaming(true)}>
                      Rename
                    </Button>
                  )}
                  <Button
                    variant={s.active ? 'danger-outline' : 'secondary'}
                    size="sm"
                    onClick={() => setToggle(true)}
                  >
                    {s.active ? 'Deactivate' : 'Activate'}
                  </Button>
                </>
              }
            />
            <Tabs<AgentTab>
              label="Agent sections"
              value={tab}
              onChange={setTab}
              tabs={[
                { value: 'access', label: 'Project access' },
                {
                  value: 'credentials',
                  label: s.owner ? 'Connections & setup' : 'Credentials & setup',
                  count: s.credentials.filter((c) => c.state === 'active').length,
                },
              ]}
            />
            <div role="tabpanel" aria-labelledby={`tab-${tab}`} className="stack">
              {tab === 'access' ? (
                <ProjectAccessCard service={s} />
              ) : (
                <>
                  <Card
                    title={s.owner ? 'Connections' : 'Credentials'}
                    subtitle={
                      s.owner
                        ? 'Each approved browser sign-in is one connection, valid for 90 days and revocable.'
                        : 'Each credential is individually identifiable, expiring and revocable. Secrets are shown once.'
                    }
                    action={
                      !s.owner && (
                        <Button size="sm" onClick={() => setIssuing(true)} disabled={!s.active}>
                          Issue credential
                        </Button>
                      )
                    }
                  >
                    {s.credentials.length === 0 ? (
                      <span className="small muted">
                        {s.owner ? 'No connections.' : 'No credentials issued.'}
                      </span>
                    ) : (
                      <div className="table-wrap">
                        <table className="table">
                          <thead>
                            <tr>
                              <th>Label</th>
                              <th>Client ID</th>
                              <th>State</th>
                              <th>Expires</th>
                              <th>Last used</th>
                              <th>Issued by</th>
                              <th />
                            </tr>
                          </thead>
                          <tbody>
                            {s.credentials.map((c) => (
                              <tr key={c.id}>
                                <td className="ink">
                                  {c.label}
                                  {c.kind === 'oauth_grant' && (
                                    <span className="small muted"> · browser sign-in</span>
                                  )}
                                </td>
                                <td className="mono small">{c.clientId}</td>
                                <td>
                                  <StateBadge state={c.state} />
                                </td>
                                <td className="small">
                                  <Timestamp value={c.expiresAt} dateOnly />
                                </td>
                                <td className="small">
                                  <Timestamp value={c.lastUsedAt} relative />
                                </td>
                                <td className="small">
                                  <ActorName actor={c.createdBy} />
                                </td>
                                <td>
                                  {c.state === 'active' && (
                                    <Button size="sm" variant="ghost" onClick={() => setRevoking(c.id)}>
                                      Revoke
                                    </Button>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </Card>
                  <AgentSetupGuide
                    setup={s.setup}
                    mode={
                      s.owner
                        ? { kind: 'personal' }
                        : {
                            kind: 'headless',
                            agentName: s.name,
                            clientId: s.credentials.find((c) => c.state === 'active')?.clientId,
                          }
                    }
                  >
                    {!s.owner && (
                      <KeyValue
                        items={[
                          [
                            'Agent API',
                            <span key="a" className="mono small">
                              {s.setup.apiBase}
                            </span>,
                          ],
                          [
                            'Token endpoint',
                            <span key="t" className="mono small">
                              {s.setup.tokenEndpoint}
                            </span>,
                          ],
                        ]}
                      />
                    )}
                    <span className="small muted">
                      {s.owner
                        ? `${s.owner.name} connects by installing the plugin in Claude Code and signing in.`
                        : 'The CLI exchanges the client ID and secret for a one-hour access token (client-credentials grant) and sends it as a bearer token.'}{' '}
                      Tokens never carry approval authority.
                    </span>
                  </AgentSetupGuide>
                </>
              )}
            </div>
            {issuing && <IssueCredential serviceId={s.id} onClose={() => setIssuing(false)} />}
            {renaming && <RenameHeadlessAgent service={s} onClose={() => setRenaming(false)} />}
            {revoking && (
              <ConfirmDialog
                title={s.owner ? 'Revoke connection?' : 'Revoke credential?'}
                confirmLabel="Yes, revoke"
                cancelLabel={s.owner ? 'Keep connection' : 'Keep credential'}
                variant="danger"
                onClose={() => setRevoking(null)}
                onConfirm={() => revoke.mutate(revoking)}
                pending={revoke.isPending}
                error={revoke.error}
              >
                <p>
                  Requests using this credential or its access tokens fail immediately. Active claims held by
                  this agent will expire and need admin recovery.
                </p>
              </ConfirmDialog>
            )}
            {toggle && (
              <ConfirmDialog
                title={s.active ? `Deactivate ${s.name}?` : `Activate ${s.name}?`}
                confirmLabel={s.active ? 'Yes, deactivate' : 'Activate'}
                variant={s.active ? 'danger' : 'primary'}
                onClose={() => setToggle(false)}
                onConfirm={() => setActive.mutate(!s.active)}
                pending={setActive.isPending}
                error={setActive.error}
              >
                <p>
                  {s.active
                    ? 'All access tokens are revoked and the agent can no longer read or write.'
                    : 'Existing unexpired credentials work again.'}
                </p>
              </ConfirmDialog>
            )}
          </>
        )}
      </QueryView>
    </div>
  );
}

/** Display name and description of a headless agent; credentials, tokens and grants are unaffected. */
function RenameHeadlessAgent({ service, onClose }: { service: AiServiceDetail; onClose: () => void }) {
  const [name, setName] = useState(service.name);
  const [description, setDescription] = useState(service.description);
  const toast = useToast();
  const save = useAction(
    () =>
      api.patch<{ version: number }>(`/admin/ai-services/${service.id}`, {
        name,
        description,
        expectedVersion: service.version,
      }),
    {
      success: 'Agent renamed',
      invalidate: [['ai-service', service.id], ['ai-services']],
      onSuccess: onClose,
    },
  );
  const nameError = save.error instanceof ApiError ? save.error.fieldError('name') : undefined;
  return (
    <Modal
      title="Rename headless agent"
      onClose={onClose}
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() =>
              save.mutate(undefined, {
                onError: (e) => {
                  if (e instanceof ApiError) toast(e.fieldError('name') ?? e.message, 'error');
                },
              })
            }
            loading={save.isPending}
            disabled={!name.trim()}
          >
            Save
          </Button>
        </>
      }
    >
      <Field label="Name" hint="Shown as the actor on comments, claims and evidence." error={nameError}>
        {(p) => <Input {...p} value={name} onChange={(e) => setName(e.target.value)} data-autofocus />}
      </Field>
      <Field label="Description" optional>
        {(p) => (
          <Textarea {...p} rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
        )}
      </Field>
      <p className="small muted">
        Credentials, client IDs, access tokens, grants and claims stay as they are.
      </p>
      {!nameError && <ErrorAlert error={save.error} />}
    </Modal>
  );
}

const CAPABILITY_COLUMNS: Record<AiCapability, string> = {
  ticket_create: 'Create tickets',
  discussion: 'Discuss',
  approval_request: 'Request approval',
  implementation: 'Implement',
  release_prepare: 'Prepare releases',
  delivery_execute: 'Deploy',
};

const sameCaps = (a: readonly AiCapability[], b: readonly AiCapability[]) =>
  a.length === b.length && a.every((c) => b.includes(c));

/**
 * Every project in one table, one checkbox per capability. Headless agents save all changed projects at once;
 * a personal agent mirrors its owner, so its table is read-only.
 */
function ProjectAccessCard({ service }: { service: AiServiceDetail }) {
  const readOnly = !!service.owner;
  const [draft, setDraft] = useState<Record<string, AiCapability[]>>({});
  const capsOf = (p: AiServiceDetail['projects'][number]) => draft[p.id] ?? p.capabilities;
  const changed = service.projects.filter((p) => draft[p.id] && !sameCaps(draft[p.id]!, p.capabilities));
  const set = (projectId: string, caps: AiCapability[]) => setDraft((d) => ({ ...d, [projectId]: caps }));
  const save = useAction(
    async () => {
      for (const p of changed)
        await api.put(`/admin/ai-services/${service.id}/grants`, {
          projectId: p.id,
          capabilities: capsOf(p),
        });
      return changed.length;
    },
    {
      success: (n) => `Access saved for ${n} project${n === 1 ? '' : 's'}`,
      invalidate: [['ai-service', service.id], ['ai-services']],
      onSuccess: () => setDraft({}),
    },
  );
  return (
    <Card
      title="Project access"
      subtitle={
        readOnly
          ? 'A personal agent mirrors its owner: every capability on the projects they can access.'
          : 'Reads are global. Tick what this agent may do in each project. No capability permits approvals, role changes, context edits, scope rewrites, overrides or claim recovery.'
      }
      action={
        !readOnly && (
          <div className="row nowrap" style={{ gap: 8 }}>
            {changed.length > 0 && (
              <Button size="sm" variant="ghost" onClick={() => setDraft({})} disabled={save.isPending}>
                Discard
              </Button>
            )}
            <Button
              size="sm"
              onClick={() => save.mutate(undefined)}
              loading={save.isPending}
              disabled={!changed.length}
            >
              {changed.length ? `Save ${changed.length} change${changed.length === 1 ? '' : 's'}` : 'Save'}
            </Button>
          </div>
        )
      }
    >
      {readOnly && (
        <Alert tone="info">
          To change what <ActorName actor={service.owner} />
          {"'s agent can reach, change their project memberships, or deactivate the agent."}
        </Alert>
      )}
      {service.projects.length === 0 ? (
        <span className="small muted">No projects yet.</span>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Project</th>
                {AI_CAPABILITIES.map((c) => (
                  <th key={c} title={AI_CAPABILITY_LABELS[c]} style={{ textAlign: 'center' }}>
                    {CAPABILITY_COLUMNS[c]}
                  </th>
                ))}
                {!readOnly && <th style={{ textAlign: 'center' }}>All</th>}
              </tr>
            </thead>
            <tbody>
              {service.projects.map((p) => {
                const caps = capsOf(p);
                const all = AI_CAPABILITIES.every((c) => caps.includes(c));
                return (
                  <tr key={p.id}>
                    <td>
                      <span className="ink">{p.key}</span> <span className="small muted">{p.name}</span>
                      {p.archived && (
                        <span className="small muted"> · archived, no effect until restored</span>
                      )}
                    </td>
                    {AI_CAPABILITIES.map((c) => (
                      <td key={c} style={{ textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          aria-label={`${AI_CAPABILITY_LABELS[c]} in ${p.key}`}
                          checked={caps.includes(c)}
                          disabled={readOnly || save.isPending}
                          onChange={(e) =>
                            set(p.id, e.target.checked ? [...caps, c] : caps.filter((x) => x !== c))
                          }
                        />
                      </td>
                    ))}
                    {!readOnly && (
                      <td style={{ textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          aria-label={`All capabilities in ${p.key}`}
                          checked={all}
                          disabled={save.isPending}
                          onChange={(e) => set(p.id, e.target.checked ? [...AI_CAPABILITIES] : [])}
                        />
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <ErrorAlert error={save.error} />
      <details className="small muted">
        <summary>What each capability allows</summary>
        <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
          {AI_CAPABILITIES.map((c) => (
            <li key={c}>
              <strong>{CAPABILITY_COLUMNS[c]}:</strong> {AI_CAPABILITY_LABELS[c]}
            </li>
          ))}
        </ul>
      </details>
    </Card>
  );
}

function IssueCredential({ serviceId, onClose }: { serviceId: string; onClose: () => void }) {
  const [label, setLabel] = useState('');
  const [days, setDays] = useState(90);
  const [copied, setCopied] = useState<string | null>(null);
  const issue = useAction(
    () =>
      api.post<{ clientId: string; clientSecret: string; expiresAt: string }>(
        `/admin/ai-services/${serviceId}/credentials`,
        { label, expiresInDays: days },
      ),
    { invalidate: [['ai-service', serviceId], ['ai-services']] },
  );
  const copy = (k: string, v: string) => navigator.clipboard.writeText(v).then(() => setCopied(k));
  if (issue.data) {
    return (
      <Modal
        title="Credential issued"
        onClose={onClose}
        dismissible={false}
        actions={<Button onClick={onClose}>I've saved these — dismiss</Button>}
      >
        <div className="secret-panel" role="alert">
          <span className="strong">Copy now — this secret will not be shown again.</span>
          <span className="small">Client ID</span>
          <div className="code">{issue.data.clientId}</div>
          <span className="small">Client secret</span>
          <div className="code">{issue.data.clientSecret}</div>
          <div className="row">
            <Button
              size="sm"
              variant="secondary"
              icon={<Copy size={14} />}
              onClick={() => copy('id', issue.data!.clientId)}
            >
              {copied === 'id' ? 'Copied' : 'Copy ID'}
            </Button>
            <Button
              size="sm"
              variant="secondary"
              icon={<Copy size={14} />}
              onClick={() => copy('secret', issue.data!.clientSecret)}
            >
              {copied === 'secret' ? 'Copied' : 'Copy secret'}
            </Button>
          </div>
        </div>
      </Modal>
    );
  }
  return (
    <Modal
      title="Issue credential"
      onClose={onClose}
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => issue.mutate(undefined)} loading={issue.isPending} disabled={!label}>
            Issue credential
          </Button>
        </>
      }
    >
      <Field label="Label" hint="Identifies where the credential is used.">
        {(p) => (
          <Input
            {...p}
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="e.g. CI/CD Pipeline"
            data-autofocus
          />
        )}
      </Field>
      <Field label="Expires in (days)">
        {(p) => (
          <Input
            {...p}
            type="number"
            min={1}
            max={365}
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
          />
        )}
      </Field>
      <ErrorAlert error={issue.error} />
    </Modal>
  );
}
